import tz_lookup from "@photostructure/tz-lookup";
import { DateTime, IANAZone } from "luxon";
import { waitForAlpineStore } from "../waitForAlpineStore";

export function Location() {
  return {
    _cacheId: "locationCache",
    latitude: null,
    longitude: null,
    timezone: null,
    altitude: null,
    accuracy: null,
    source: null,
    location: null,

    geocodingProvider: null, // ← add this

    isGPSAvailable: false,

    // Tracks status: 'prompt', 'granted', or 'denied'
    permissionState: "prompt",

    openLeaflet: false,
    gpsOptions: {
      enableHighAccuracy: true,
      timeout: 5 * 60 * 1000,
      maximumAge: 60 * 60 * 1000,
    },

    // -------------------------------------------------------------
    // INIT: load cached and then attempt GPS
    // -------------------------------------------------------------
    async init() {
      const cacheLoaded = this.loadFromCache();
      const onboarding = localStorage.getItem("onboarding_completed");

      if (onboarding === null || onboarding === false) {
        return;
      }
      // Call the new query function immediately
      await this._queryPermissionStatus();

      if (!cacheLoaded) {
        await this.queryCoordinates();
      }
      // this.loadFromCache();
    },

    async queryCoordinates() {
      // Only now request GPS if no valid cache exists
      // Optionally, you could show a UI prompt instead of auto-fetching
      console.log("No cached location — user may trigger GPS manually");
      Alpine.store("notification").showNotification(
        "Querying GPS",
        "Querying GPS Location. Please wait.",
        "info",
        false,
      );
      await this.setGPSCoordinatesFromDevice(); // optional auto-gps
      Alpine.store("notification").hide();
      if (!this.latitude || !this.longitude) {
        // something went wrong with GPS - set to default and implicitly open leaflet modal
        this._setDefaultCoordinates();

        Alpine.store("notification").showNotification(
          "Default location set to Sharada Peetham, Sringeri",
          "GPS unavailable. Using default location — tap the pin icon to change it.",
          "info",
          5, // auto-dismiss after 5 seconds
        );

        Alpine.store("notification").hide();
        Alpine.store("notification").showNotification(
          "Select location",
          "Location access failed. Use the map to set your location manually.",
          "info",
          -1,
        );

        waitForAlpineStore("locationPicker")
          .then((store) => {
            store.openModal(); // safe: store exists now
          })
          .catch((err) => {
            console.warn(err);
          });
      }
    },

    _setDefaultCoordinates() {
      this.latitude = 13.418904601017863;
      this.longitude = 75.25080084800722;
      this.timezone = this._computeTimeZone(this.latitude, this.longitude);
      this.altitude = 670;
      this.accuracy = 0.1;
    },

    // -------------------------------------------------------------
    // CACHE
    // -------------------------------------------------------------
    loadFromCache() {
      const raw = localStorage.getItem(this._cacheId);
      if (!raw) {
        console.log(
          "No previous cache found. Proceeding to acquire location via GPS.",
        );
        return false;
      }

      try {
        // because cache has been set after thorough verification
        // ...
        const cache = JSON.parse(raw);
        this.latitude = cache.latitude;
        this.longitude = cache.longitude;
        this.timezone = cache.timezone;
        this.altitude = cache.altitude;
        this.accuracy = cache.accuracy;
        this.location = cache.location;
        this.source = cache.source;

        return true;
      } catch (e) {
        console.error("Invalid location cache", e);
        return false;
      }

      return true;
    },

    saveToCache() {
      if (
        this.latitude === null ||
        this.longitude === null ||
        this.timezone === null ||
        this.accuracy === null
      ) {
        return false;
      }

      const cache = {
        latitude: parseFloat(this.latitude),
        longitude: parseFloat(this.longitude),
        timezone: this.timezone,
        altitude: parseFloat(this.altitude),
        accuracy: this.accuracy,
        location: this.location,
        source: this.geocodingProvider,
      };

      if (!this.validCoordinates(cache.latitude, cache.longitude)) {
        console.log("invalid coordinates ", cache);
        return false;
      }

      if (!this.validTimeZone(cache.timezone)) {
        console.log("Invalid Timezone ", cache.timezone);
        return false;
      }

      localStorage.setItem(this._cacheId, JSON.stringify(cache));
      return true; // ← add this
    },

    handleSaveToCache() {
      if (!this.saveToCache()) {
        Alpine.store("notification").showNotification(
          "Location Error",
          "Failed to save location cache due to invalid data.",
          "error",
          -1,
        );
      }
    },

    async setGPSCoordinatesForceOverrideCache() {
      const cacheSeconds = this.gpsOptions["maximumAge"];
      this.gpsOptions["maximumAge"] = 0;
      await this.setGPSCoordinatesFromDevice();
      this.gpsOptions["maximumAge"] = cacheSeconds;
    },

    // New Permissions API
    // -------------------------------------------------------------
    // Permissions API Helper: Queries current status
    // -------------------------------------------------------------
    async _queryPermissionStatus() {
      if (!("permissions" in navigator)) {
        console.warn("Permissions API not supported in this browser.");
        return;
      }
      try {
        const status = await navigator.permissions.query({
          name: "geolocation",
        });
        this.permissionState = status.state; // Update Alpine state

        // Optional: Listen for changes if the user updates permissions in system settings
        status.onchange = () => {
          this.permissionState = status.state;
          if (status.state === "denied") {
            Alpine.store("notification").showNotification(
              "Info",
              "Location permission was manually revoked in settings.",
              "info",
              -1,
            );
          }
        };
      } catch (error) {
        console.error("Error querying permissions:", error);
      }
    },

    // -------------------------------------------------------------
    // MAIN GPS FLOW (async)
    // -------------------------------------------------------------
    async setGPSCoordinatesFromDevice() {
      // Check permission state early
      if (this.permissionState === "denied") {
        const onboarding = localStorage.getItem("onboarding_completed");
        if (onboarding) {
          Alpine.store("notification").showNotification(
            "Access Denied",
            "Location access is blocked. Enable GPS in browser settings manually.",
            "error",
            -1,
          );
          Alpine.nextTick(() => {
            waitForAlpineStore("locationPicker").then((store) =>
              store.openModal(),
            );
          });
        }
        return;

        Alpine.store("notification").showNotification(
          "Access Denied",
          "Location access is blocked. Enable GPS in browser settings manually.",
          "error",
          -1,
        );
        // If permanently denied, open the map UI automatically for the user to set it manually
        waitForAlpineStore("locationPicker").then((store) => store.openModal());

        return; // Exit early without attempting navigator.geolocation call
      }

      try {
        Alpine.store("notification").showNotification(
          "Location",
          "Seeking GPS coordinates...",
          "progress",
        );

        const coords = await this.getGPSCoordinatesAsync();
        console.log(coords);

        if (!this.validCoordinates(coords.latitude, coords.longitude)) {
          console.log("invalid coordinate", coords);
          console.warn("Invalid coordinates received from GPS:", coords);
          Alpine.store("notification").showNotification(
            "Location Error",
            "Received invalid GPS coordinates. Using last known valid location.",
            "error",
            -1,
          );

          return; // exit without updating anything
        }
        this.latitude = parseFloat(coords.latitude);
        this.longitude = parseFloat(coords.longitude);
        this.timezone = this._computeTimeZone(this.latitude, this.longitude);
        let tmp = await this._getLocationFromCoordinates(
          this.latitude,
          this.longitude,
        );
        // Create the subset object inline
        this.location = {
          city: tmp.city,
          state: tmp.state,
          country: tmp.country,
        };
        this.altitude = parseFloat(tmp.altitude);
        this.source = tmp.source;

        waitForAlpineStore("locationPicker").then((store) =>
          store.updateLocationonMap(this.latitude, this.longitude),
        );

        this.handleSaveToCache();

        Alpine.store("notification").showNotification(
          "Location",
          "GPS coordinates acquired.",
          "success",
          1,
        );
      } catch (error) {
        // Errors handled inside getGPSCoordinatesAsync
        console.warn("GPS failed:", error.message);
        if (
          error.code === error.PERMISSION_DENIED ||
          error.code === error.POSITION_UNAVAILABLE
        ) {
          waitForAlpineStore("locationPicker").then((store) =>
            store.openModal(),
          );
        }
      }
    },

    // -------------------------------------------------------------
    // Promise wrapper for navigator.geolocation
    // -------------------------------------------------------------
    getGPSCoordinatesAsync() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error("Geolocation not supported"));
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (pos) => resolve(pos.coords),
          (err) => {
            this._handleError(err);
            reject(err);
          },
          this.gpsOptions,
        );
      });
    },

    // -------------------------------------------------------------
    // ERROR HANDLING
    // -------------------------------------------------------------
    _handleError(error) {
      console.error("Geolocation error:", error.message);

      let msg = "";
      switch (error.code) {
        case error.PERMISSION_DENIED:
          msg = "Location permission denied. Please allow access.";
          // CRITICAL: Update the state so future clicks on the GPS button know it's 'denied'
          this.permissionState = "denied";
          break;
        case error.POSITION_UNAVAILABLE:
          msg = "Position unavailable. Check GPS settings.";
          break;
        case error.TIMEOUT:
          msg = "GPS request timed out. Try again.";
          break;
        default:
          msg = "Could not get your location.";
      }

      Alpine.store("notification").showNotification(
        "Location Error",
        msg,
        "error",
        -1,
      );
    },

    // -------------------------------------------------------------
    // TIMEZONE COMPUTE
    // -------------------------------------------------------------
    _computeTimeZone(lat, lon) {
      const tz = tz_lookup(parseFloat(lat), parseFloat(lon));
      return IANAZone.isValidZone(tz) ? tz : "UTC";
    },

    manualUpdateTimeZone(tz) {
      if (tz && this.validTimeZone(tz)) {
        this.timezone = tz;
        this.handleSaveToCache();
      } else {
        console.log("Invalid Timezone identifier " + tz);
      }
    },

    manualUpdateLocation(latitude, longitude, timezone) {
      this.latitude = parseFloat(latitude);
      this.longitude = parseFloat(longitude);
      if (!timezone || !this.validTimeZone(timezone)) {
        console.log("Overriding timezone as invlid " + timezone);
        timezone = this._computeTimeZone(this.latitude, this.longitude);
      }
      this.timezone = timezone;
      this.handleSaveToCache();
    },

    validCoordinates(latitude, longitude) {
      const lat = parseFloat(latitude);
      const lon = parseFloat(longitude);

      if (Number.isNaN(lat) || Number.isNaN(lon)) return false;

      return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
    },

    validTimeZone(timezone) {
      return IANAZone.isValidZone(timezone);
    },

    evaluateGPSQuality() {
      const horizontal = this.accuracy;
      const vertical = this.altitudeAccuracy;

      let level = "good";

      if (horizontal < 0 || vertical < 0) {
        level = "unreliable";
      } else if (horizontal > 150 || vertical > 100 || horizontal < 0) {
        level = "poor";
      } else if (horizontal > 50 || vertical > 20) {
        level = "ok";
      } else if (horizontal < 10 && vertical < 10) {
        level = "excellent";
      }

      return level;
    },

    async saveLocationFromOSM(latitude, longitude) {
      console.log(`Latitude: ${latitude}, Longitude: ${longitude}`);

      this.latitude = parseFloat(latitude);
      this.longitude = parseFloat(longitude);
      this.accuracy = 1;
      this.timezone = this._computeTimeZone(latitude, longitude);
      let tmp = await this._getLocationFromCoordinates(
        this.latitude,
        this.longitude,
      );
      // Create the subset object inline
      this.location = {
        city: tmp.city,
        state: tmp.state,
        country: tmp.country,
      };
      this.altitude = parseFloat(tmp.altitude);
      this.geocodingProvider = tmp.source;
      this.saveToCache();
    },

    get DST() {
      if (this.timezone) {
        const now = DateTime.now().setZone(this.timezone);
        if (!now.isValid) return false;
        return now.isInDST;
      }
      return false;
    },

    async _getLocationFromCoordinates(latitude, longitude) {
      try {
        const res = await fetch("/api/geo", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lat: latitude, lon: longitude }),
        });

        if (!res.ok) {
          throw new Error(`Geo API failed: ${res.status}`);
        }

        const { city, state, country, altitude, source } = await res.json();
        return { city, state, country, altitude, source };
      } catch (err) {
        console.error(err);
        return null;
      }
    },

    isGPSAvailable() {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "granted") {
          // Permission already exists!
          // You can call getCurrentPosition() now and it won't show a dialog.
          this.isGPSAvailable = true;
        } else if (result.state === "prompt") {
          // The user hasn't been asked yet.
          // DO NOT call getCurrentPosition() here yet.
          // Instead, show your custom "GPS needed" UI or FAB.
        } else if (result.state === "denied") {
          // User blocked it. Show a "How to enable" guide or open your map.

          this.isGPSAvailable = false;
        }

        // Listen for changes (e.g., if the user goes to settings and turns it on)
        result.onchange = () => {
          console.log("Permission changed to: ", result.state);
        };
      });
    },
  };
}
